import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listPendingRtw, decideRtw, getRtwAuditLog } from "@/lib/rtw.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type AuditEvent = { id: number; event: string; actor_id: string | null; actor_role: string; notes: string | null; created_at: string };

export const Route = createFileRoute("/_authenticated/admin/rtw")({
  head: () => ({ meta: [{ title: "Right-to-Work Review — Admin" }] }),
  component: AdminRtwPage,
});

type Sub = {
  id: string;
  full_name: string | null;
  email: string | null;
  status: string;
  share_code: string | null;
  dob: string | null;
  submitted_at: string | null;
  notes: string | null;
  document_url: string | null;
};

function AdminRtwPage() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const list = useServerFn(listPendingRtw);
  const decide = useServerFn(decideRtw);
  const fetchAudit = useServerFn(getRtwAuditLog);

  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState<Sub[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [auditFor, setAuditFor] = useState<string | null>(null);
  const [auditRows, setAuditRows] = useState<AuditEvent[]>([]);
  const [signedTtl, setSignedTtl] = useState<number>(300);

  const load = async () => {
    setLoading(true);
    const r = await list();
    if ("error" in r) {
      toast.error(r.error);
      navigate({ to: "/dashboard", replace: true });
      return;
    }
    setSubs(r.submissions as Sub[]);
    setSignedTtl(r.signed_url_ttl ?? 300);
    setLoading(false);
  };

  const openAudit = async (userId: string) => {
    setAuditFor(userId);
    setAuditRows([]);
    const r = await fetchAudit({ data: { bartender_id: userId } });
    if ("error" in r) {
      toast.error(r.error);
      return;
    }
    setAuditRows(r.events as AuditEvent[]);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) {
        navigate({ to: "/dashboard", replace: true });
        return;
      }
      await load();
    })();
  }, []);

  const act = async (userId: string, decision: "verify" | "reject") => {
    setBusyId(userId);
    try {
      const r = await decide({
        data: { user_id: userId, decision, notes: notes[userId]?.trim() || undefined },
      });
      if ("error" in r) throw new Error(r.error);
      toast.success(decision === "verify" ? "RTW verified." : "Marked for resubmission.");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't update.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <p className="tracking-wide-luxury text-accent">Admin</p>
        <h1 className="mt-3 font-display text-3xl">Right-to-Work Review</h1>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Confirm each candidate's right to work in the UK before they accept
          paid engagements. Verifications default to a 5-year expiry. Document
          links expire after {Math.round(signedTtl / 60)} minutes.
        </p>

        {loading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
        ) : subs.length === 0 ? (
          <p className="mt-8 text-sm text-muted-foreground">Nothing in the queue.</p>
        ) : (
          <ul className="mt-8 space-y-6">
            {subs.map((s) => (
              <li key={s.id} className="border border-border bg-card p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-lg">
                      {s.full_name || "Unnamed candidate"}
                    </p>
                    <p className="text-xs text-muted-foreground">{s.email}</p>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Submitted{" "}
                      {s.submitted_at
                        ? new Date(s.submitted_at).toLocaleString("en-GB")
                        : "—"}
                      {s.dob ? ` · DOB ${s.dob}` : ""}
                      {s.share_code ? ` · Share code ${s.share_code}` : ""}
                    </p>
                  </div>
                  <span
                    className={`border px-3 py-1 text-[10px] tracking-wide-luxury ${
                      s.status === "rejected"
                        ? "text-destructive border-destructive/40"
                        : "text-amber-400 border-amber-400/40"
                    }`}
                  >
                    {s.status === "rejected" ? "Previously rejected" : "Awaiting review"}
                  </span>
                </div>

                {s.document_url ? (
                  <a
                    href={s.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-block text-xs tracking-wide-luxury text-accent underline"
                  >
                    Open document ↗
                  </a>
                ) : (
                  <p className="mt-4 text-xs text-muted-foreground">No document on file.</p>
                )}

                <Textarea
                  value={notes[s.id] ?? ""}
                  onChange={(e) =>
                    setNotes((n) => ({ ...n, [s.id]: e.target.value }))
                  }
                  placeholder="Reviewer notes (shown to candidate on reject)"
                  className="mt-4 min-h-[72px]"
                />

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={() => act(s.id, "verify")} disabled={busyId === s.id}>
                    Verify
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => act(s.id, "reject")}
                    disabled={busyId === s.id}
                  >
                    Reject
                  </Button>
                  <Button variant="ghost" onClick={() => openAudit(s.id)}>
                    Audit log
                  </Button>
                </div>

                {auditFor === s.id && (
                  <div className="mt-4 border border-border bg-background/40 p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] tracking-wide-luxury text-muted-foreground">
                        Audit history
                      </p>
                      <button
                        onClick={() => setAuditFor(null)}
                        className="text-[10px] tracking-wide-luxury text-muted-foreground hover:text-foreground"
                      >
                        Close
                      </button>
                    </div>
                    {auditRows.length === 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">No entries.</p>
                    ) : (
                      <ul className="mt-2 divide-y divide-border text-xs">
                        {auditRows.map((a) => (
                          <li key={a.id} className="py-2">
                            <p>
                              <span className="text-accent">{a.event}</span>
                              <span className="text-muted-foreground"> · {a.actor_role}</span>
                            </p>
                            <p className="text-muted-foreground">
                              {new Date(a.created_at).toLocaleString("en-GB")}
                              {a.notes ? ` — ${a.notes}` : ""}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
